import React, {Fragment, useState, useRef} from 'react';
import moment from 'moment';
import {useParams} from 'react-router-dom';
import {
  Button, Classes, Colors, Intent, Icon, MenuItem, Popover, Label, Tag,
  NonIdealState,
} from '@blueprintjs/core';
import {Suggest} from '@blueprintjs/select';

import {useQuery, useLazyQuery} from '@apollo/react-hooks';
import {gql} from 'apollo-boost';
import {Breadcrumbs, Loading, SortableTableHead, compareColumns} from 'worksource-ui';

import withMainLayout from 'layouts/main/view';
import {showGQLError} from 'utils/toaster';
import {useUIActions} from 'utils/ui-context';
import UsersEditDialog from 'dialogs/UsersEditDialog';
import HeaderBar from 'components/header-bar';

const renderTag = ({value, label, icon}, {modifiers, handleClick}) => {
  return (
    <MenuItem
      active={modifiers.active}
      key={value}
      onClick={handleClick}
      text={label}
      icon={icon || 'tag'}
      shouldDismissPopover={false}
    />
  );
};


const FETCH_USERS = gql`
  query FetchAllUsers($clientId: ID!) {
    users(clientId: $clientId) {
      id,
      fullName,
      email,
      tags,
      lastLoggedInAt,
      role,
    },
    clientSubscription(clientId: $clientId) {
      totalSeats,
      seatsOccupied,
    },
    tags(clientId: $clientId),
    currentUser @client {
      superuser,
    },
  }
`;

const FETCH_EXPORT = gql`
  query fetchExport($clientId: ID!) {
    usersCsv(clientId: $clientId),
  }
`;

const UsersIndexPage = () => {
  ////
  // State/hooks
  //
  const {setDialog} = useUIActions();
  const params = useParams();
  const [currentSort, setCurrentSort] = useState({column: 'email', direction: 'asc'});
  const [filterTag, setFilterTag] = useState(null);
  const filterTagInput = useRef();

  ////
  // GraphQL
  //
  const {loading: loadingFetch, data} = useQuery(FETCH_USERS, {
    variables: {clientId: params.clientId},
    onError: error => showGQLError({error}),
  });
  const [fetchExport, {
    loading: loadingExport,
    refetch: refetchExport,
    called: calledExport,
  }] = useLazyQuery(FETCH_EXPORT, {
    variables: {clientId: params.clientId},
    notifyOnNetworkStatusChange: true,
    onError: error => showGQLError({error}),
    onCompleted: ({usersCsv}) => {
      const csvContent = `data:text/csv;charset=utf-8,${usersCsv}`;
      const el = document.createElement('a');
      el.setAttribute('href', encodeURI(csvContent));
      el.setAttribute('download', `WorkSource users ${moment().format('YYYY-MM-DD')}.csv`);
      document.body.appendChild(el);
      el.click();
    },
  });

  ////
  // Events
  //
  const editUser = ({id}) => {
    setDialog(() => (
      <UsersEditDialog
        userId={id}
        clientId={params.clientId}
      />
    ));
  };

  const createUser = () => {
    setDialog(() => (
      <UsersEditDialog clientId={params.clientId} />
    ));
  };

  const exportCsv = () => {
    if (calledExport) {
      refetchExport();
    } else {
      fetchExport();
    }
  };

  ////
  // Render
  //
  if (loadingFetch || !data) {
    return (
      <Loading />
    );
  }

  const {users, clientSubscription: subscription, tags, currentUser} = data;

  const sortedUsers = (() => {
    if (!currentSort) return users;

    let sortFunction = compareColumns(currentSort.column);
    if (currentSort.column === 'last_logged_in_at') {
      sortFunction = (left, right) => {
        left = left.lastLoggedInAt;
        right = right.lastLoggedInAt;

        if (!left && right) return -1;
        if (left && !right) return 1;
        if (!left && !right) return 0;

        const a = moment(left);
        const b = moment(right);
        if (a.isAfter(b)) return 1;
        if (b.isAfter(a)) return -1;
        return 0;
      };
    }

    let sorted = users.sort(sortFunction);
    if (currentSort.direction === 'desc') {
      return sorted.reverse();
    }

    if (filterTag === '__no_tags') {
      sorted = sorted.filter(user => user.tags.length === 0);
    } else if (filterTag) {
      sorted = sorted.filter(user => user.tags.includes(filterTag));
    }

    return sorted;
  })();

  const goodStanding = subscription && subscription.seatsOccupied <= subscription.totalSeats;

  // Prevent the demo user from fetching users
  if (params.clientId === '20efc8fc-efd3-49f9-86d6-1c98acefed92' && !currentUser.superuser) {
    return (
      <NonIdealState
        icon="box"
        title="Demo mode"
        description="The users tab is disabled while in demo mode. If you'd like a preview of our advanced user management capabilities, please let us know and we'll set you up with a fully-activated trial."
      />
    );
  }

  return (
    <Fragment>
      <HeaderBar>
        <Breadcrumbs>
          <Breadcrumbs.Breadcrumb
            current
            href={`/clients/${params.clientId}/users`}
            icon="user"
            text="Users"
          />
        </Breadcrumbs>
        <div className="spacer" />
        <Button
          text="Export CSV"
          icon="th-list"
          onClick={exportCsv}
          disabled={loadingExport}
          loading={loadingExport}
        />
        <Button
          intent={Intent.PRIMARY}
          text="New User"
          icon="plus"
          onClick={createUser}
        />
      </HeaderBar>

      <div className="content-container">
        <div className="layout--full">
          <table className="bp3-html-table bp3-html-table-striped bp3-interactive sortable">
            <thead>
              <tr>
                <SortableTableHead
                  label="Full name"
                  column="full_name"
                  currentSort={currentSort}
                  setCurrentSort={setCurrentSort}
                />
                <SortableTableHead
                  label="Email address"
                  column="email"
                  currentSort={currentSort}
                  setCurrentSort={setCurrentSort}
                />
                <th className={filterTag ? 'sorting' : ''}>
                  <Popover
                    fill={false}
                    popoverClassName={Classes.POPOVER_CONTENT_SIZING}
                    onOpened={() => filterTagInput.current.focus()}
                    content={(
                      <Label text="Filter tag">
                        <Suggest
                          inputProps={{inputRef: r => filterTagInput.current = r}}
                          items={[
                            {label: 'All users', value: '__all', icon: 'list'},
                            {label: 'No tags', value: '__no_tags', icon: 'cross'},
                            ...tags.map(tag => ({
                              label: tag,
                              value: tag,
                            })),
                          ]}
                          itemPredicate={(query, {value}) => {
                            return value.toLowerCase().includes(query.toLowerCase());
                          }}
                          itemRenderer={renderTag}
                          inputValueRenderer={tag => tag.label}
                          value={filterTag}
                          noResults={<MenuItem disabled text="No results" />}
                          popoverProps={{minimal: true}}
                          onItemSelect={({value}) => {
                            if (value === '__all') {
                              setFilterTag(null);
                            } else {
                              setFilterTag(value);
                            }
                          }}
                        />
                      </Label>
                    )}
                  >
                    <Fragment>
                      Tags
                      {filterTag && (
                        <Fragment>
                          &nbsp;
                          <Icon icon="filter" />
                        </Fragment>
                      )}
                    </Fragment>
                  </Popover>
                </th>
                <SortableTableHead
                  label="Last logged in"
                  column="last_logged_in_at"
                  currentSort={currentSort}
                  setCurrentSort={setCurrentSort}
                />
                <SortableTableHead
                  label="Role"
                  column="role"
                  currentSort={currentSort}
                  setCurrentSort={setCurrentSort}
                />
              </tr>
            </thead>
            <tbody>
              {sortedUsers.map((user) => {
                return <tr
                  key={user.id}
                  onClick={() => editUser({id: user.id})}
                >
                  <td>{user.fullName}</td>
                  <td>{user.email}</td>
                  <td className="users-index--tags">
                    {user.tags.map((tag) => {
                      return (
                        <Tag key={tag} minimal>{tag}</Tag>
                      );
                    })}
                  </td>
                  <td>
                    {user.lastLoggedInAt ?
                        moment(user.lastLoggedInAt).fromNow() : 'Never'
                    }
                  </td>
                  <td className="users-index--role">{user.role}</td>
                </tr>;
              })}
            </tbody>
          </table>
          {subscription && (
            <div className="users-index--subscription">
              <div className="tick">
                <Icon
                  icon={goodStanding ? 'tick' : 'warning-sign'}
                  color={goodStanding ? Colors.GREEN2 : Colors.ORANGE4}
                />
              </div>
              <div className="text">
                {subscription.seatsOccupied} users out of {subscription.totalSeats} available seats.
                {!goodStanding && (
                  <Fragment>
                    <br />
                    Your subscription will be automatically updated at the next billing cycle.
                  </Fragment>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </Fragment>
  );
};

export default withMainLayout()(UsersIndexPage);
